import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import { LatLngBoundsExpression } from 'leaflet';
import { MapPinIcon, MaximizeIcon } from 'lucide-react';
import { serviceAreaLocations } from '../data/content';
type MapFocusProps = {
  selected: string | null;
  bounds: LatLngBoundsExpression;
};
function MapFocus({
  selected,
  bounds
}: MapFocusProps) {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const resize = () => map.invalidateSize();
    const timer = window.setTimeout(resize, 150);
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [map]);
  useEffect(() => {
    const area = serviceAreaLocations.find(a => a.name === selected);
    if (area) {
      map.flyTo([area.lat, area.lng], 11, {
        duration: 0.6
      });
    } else {
      map.flyToBounds(bounds, {
        padding: [40, 40],
        duration: 0.6
      });
    }
  }, [selected, bounds, map]);
  return null;
}
export function ServiceAreaMap() {
  const [selected, setSelected] = useState<string | null>(null);
  const bounds = useMemo<LatLngBoundsExpression>(() => serviceAreaLocations.map(a => [a.lat, a.lng] as [number, number]), []);
  const toggle = (name: string) => setSelected(cur => cur === name ? null : name);
  useEffect(() => {
    const onSelect = (e: Event) => {
      const name = (e as CustomEvent<string>).detail;
      if (serviceAreaLocations.some(a => a.name === name)) setSelected(name);
    };
    window.addEventListener('select-service-area', onSelect);
    return () => window.removeEventListener('select-service-area', onSelect);
  }, []);
  return <div id="service-areas" className="mt-20 grid scroll-mt-24 gap-8 border-t border-stone-line pt-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20" data-id="element-277" data-mp="33pffc3uepwn3">
      <div data-id="element-278" data-mp="38hqgtb4spqhr">
        <h3 className="font-display text-2xl font-bold text-forest md:text-3xl" data-id="element-279" data-mp="38i8l8v1m3b0r">Proudly serving North Dallas</h3>
        <p className="mt-4 leading-relaxed text-ink-muted" data-id="element-280" data-mp="3ddb9x1nftr4x">
          Nine communities, one local crew. Pick a city to see it on the map.
        </p>
        <ul className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3" data-id="element-281" data-mp="38cwyraqji12o">
          {serviceAreaLocations.map(area => {
          const active = selected === area.name;
          return <li key={area.name} data-id="element-282" data-mp="2ze1rrwfo8myb">
                <button type="button" aria-pressed={active} onClick={() => toggle(area.name)} className={`flex w-full items-center gap-2 whitespace-nowrap rounded-md border px-3 py-2.5 text-left text-sm font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest ${active ? 'border-brick bg-brick text-white' : 'border-stone-line bg-white text-ink hover:border-forest/40'}`} data-id="element-283" data-mp="2zf4io4czupe8">
                  <MapPinIcon className={`h-4 w-4 shrink-0 ${active ? 'text-white' : 'text-leaf'}`} aria-hidden="true" data-id="element-284" data-mp="2bnpd82px7ryr" />
                  {area.name}
                </button>
              </li>;
        })}
        </ul>
        <button type="button" onClick={() => setSelected(null)} disabled={!selected} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-forest transition-opacity duration-150 disabled:opacity-40" data-id="element-285" data-mp="w3yl2oxvm3wa">
          <MaximizeIcon className="h-4 w-4" aria-hidden="true" data-id="element-286" data-mp="15567a8n4bea0" />
          Show all areas
        </button>
      </div>

      <div className="relative z-0 h-[380px] overflow-hidden rounded-2xl border border-stone-line bg-stone-soft md:h-[460px]" data-id="element-287" data-mp="8dcdszvp4nnc">
        <MapContainer bounds={bounds} boundsOptions={{
        padding: [40, 40]
      }} scrollWheelZoom={false} className="h-full w-full" aria-label="Map of Dany Landscaping service areas" data-id="element-288" data-mp="hgdg0phfxulq">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} data-id="element-289" data-mp="3kx3r3irvqxua" />
          {serviceAreaLocations.map(area => {
          const active = selected === area.name;
          return <CircleMarker key={`${area.name}-${active}`} center={[area.lat, area.lng]} radius={active ? 12 : 9} pathOptions={{
            color: '#ffffff',
            weight: 3,
            fillColor: active ? '#c1272d' : '#1a472a',
            fillOpacity: 1
          }} eventHandlers={{
            click: () => toggle(area.name)
          }} data-id="element-290" data-mp="1re3id47pwdnb">
                <Tooltip permanent direction="top" offset={[0, -10]} className={`area-tooltip ${active ? 'area-tooltip--active' : ''}`} data-id="element-291" data-mp="2f2ee5syk3n4q">
                  {area.name}
                </Tooltip>
              </CircleMarker>;
        })}
          <MapFocus selected={selected} bounds={bounds} data-id="element-292" data-mp="2o29o5ugdlc36" />
        </MapContainer>
      </div>
    </div>;
}